import cx from 'classnames';
import {Fragment, useCallback, useContext, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, Card, Divider, DotLineDivider, Icon} from 'components';
import {ACCOUNT_PLAN_NAME_TRANSLATE} from 'constants/translate';
import {UserContext} from 'context';
import {TRANSLATION_KEYS} from 'locale';
import {BillingPlatformSubscription} from 'types';
import {billingPlatformUtils} from 'utils';
import {formatDate, transformUtils} from 'utils/transform';

import {ConfirmPauseModal} from './ConfirmPauseModal';
import {ConfirmRemovePauseModal} from './ConfirmRemovePauseModal';
import {EditSubscriptionModal} from './EditSubscriptionModal';
import {SubscriptionStatus} from './SubscriptionStatus';
import {UpgradePlanModal} from './UpgradePlanModal';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.BILLING.SUBSCRIPTION;

type Props = {
    subscription: BillingPlatformSubscription;
    hasPaymentWarning?: boolean;
    subscriptionNextPeriod: BillingPlatformSubscription;
    userCount: number;
    onChange: () => void;
}

export const SubscriptionInfo: React.FC<Props> = ({
    subscription, hasPaymentWarning, subscriptionNextPeriod, userCount, onChange
}) => {
    const {t} = useTranslation();
    const {settings: {common: {timeZone, dateFormat}}} = useContext(UserContext);
    const [editModalProps, setEditModalProps] = useState<{
        open: boolean,
        endOfTerm?: boolean,
        minUsers?: number,
        hasPaymentWarning?: boolean,
        subscription?: BillingPlatformSubscription;
        subscriptionNextPeriod?: BillingPlatformSubscription;
    }>({open: false});
    const [pauseSubscriptionModalProps, setPauseSubscriptionModalProps] = useState<{
        open: boolean;
        hasScheduledChanges?: boolean;
    }>({open: false});
    const [removePauseSubscriptionModalOpen, setRemovePauseSubscriptionModalOpen] = useState(false);

    const isPlanWithUpgrade = useMemo(() => billingPlatformUtils.isUpgradablePlan(subscription.planId), [subscription]);
    const isPlanWithBillingPlatformPayment = useMemo(() =>
        billingPlatformUtils.isPlanWithBillingPlatformPayment(subscription.planId), [subscription]);
    const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

    const subscriptionHeader = useMemo(() => {
        const planName = t(ACCOUNT_PLAN_NAME_TRANSLATE[subscription.planId]);
        return isPlanWithBillingPlatformPayment ? `${planName} ${subscription.currency} ${subscription.billingPeriodUnit}` : planName;
    }, [isPlanWithBillingPlatformPayment, subscription, t]);

    const handleEditSubscriptionModalOpen = useCallback(({endOfTerm}: {endOfTerm: boolean}) => {
        setEditModalProps({open: true, subscription, subscriptionNextPeriod, minUsers: userCount, hasPaymentWarning, endOfTerm});
    }, [subscription, subscriptionNextPeriod, userCount, hasPaymentWarning]);

    const handleEditSubscriptionModalClose = useCallback((confirm: boolean) => {
        if (confirm) {
            onChange();
        }
        setEditModalProps({open: false, subscription: null});
    }, [onChange]);

    const renderPrice = useCallback((s: BillingPlatformSubscription) => (
        <Fragment>
            <DotLineDivider className='-sm:tw-hidden tw-bg-dark' />
            <span>{t(T_KEYS.PRICE)}</span>
            <span className='tw-font-bold tw-ml-1'>
                {transformUtils.formatPrice(s.amount, s.currency)}
            </span>
        </Fragment>
    ), [t]);

    const renderUnitPrice = useCallback((s: BillingPlatformSubscription) => (
        <span className='tw-font-bold tw-ml-1'>
            {`(${transformUtils.formatPrice(s.unitPrice, s.currency)} ${t(T_KEYS.PER_USER)})`}
        </span>
    ), [t]);

    const renderUsers = useCallback((s: BillingPlatformSubscription, showUserCount: boolean) => (
        <div className='tw-flex tw-items-center'>
            <Icon name='agent' />
            <span className='tw-mx-1'>{t(T_KEYS.USERS)}</span>
            {showUserCount ?
                <span>{`${userCount} ${t(T_KEYS.OF)} ${s.quantity}`}</span> :
                <span>{s.quantity}</span>}
            {renderUnitPrice(s)}
        </div>
    ), [renderUnitPrice, userCount, t]);

    const formatSubscriptionDate = useCallback((inputDate: Date) =>
        formatDate(inputDate, timeZone, dateFormat), [timeZone, dateFormat]);

    const openConfirmPauseSubscriptionModal = useCallback(() => {
        setPauseSubscriptionModalProps({open: true, hasScheduledChanges: subscription?.hasScheduledChanges});
    }, [subscription]);

    const handlePauseSubscriptionModalClose = useCallback((confirm: boolean) => {
        setPauseSubscriptionModalProps({open: false});
        if (confirm) {
            onChange();
        }
    }, [onChange]);

    const openConfirmRemovePauseSubscriptionModal = useCallback(() => {
        setRemovePauseSubscriptionModalOpen(true);
    }, []);

    const handleConfirmRemovePauseSubscriptionModalClose = useCallback((confirm: boolean) => {
        setRemovePauseSubscriptionModalOpen(false);
        if (confirm) {
            onChange();
        }
    }, [onChange]);

    const handleEditSubscription = useCallback((options: {endOfTerm: boolean}) => {
        if (isPlanWithUpgrade) {
            setUpgradeModalOpen(true);
        } else {
            handleEditSubscriptionModalOpen(options);
        }
    }, [isPlanWithUpgrade, handleEditSubscriptionModalOpen]);

    const renderRemoveScheduledPauseBtn = useCallback((className: string) => (
        <Button
            color='primary'
            width='large'
            className={cx('tw-ts-h5-bold', className)}
            icon='calendar-remove'
            onClick={openConfirmRemovePauseSubscriptionModal}
        >
            {t(T_KEYS.REMOVE_SCHEDULED_PAUSE_MODAL.TITLE)}
        </Button>
    ), [t, openConfirmRemovePauseSubscriptionModal]);

    const renderPauseSubscriptionBtn = useCallback((className: string) => (
        <Button
            width='large'
            className={cx('tw-ts-h5-bold', className)}
            onClick={openConfirmPauseSubscriptionModal}
        >
            {t(T_KEYS.PAUSE_SCHEDULE_MODAL.TITLE)}
        </Button>
    ), [t, openConfirmPauseSubscriptionModal]);

    return (
        <Card key={subscription.id} className='tw-mb-4'>
            <div className='tw-flex tw-items-center tw-mb-4 -sm:tw-flex-wrap'>
                <div className='tw-ts-h3-bold -md:tw-ts-h5-bold tw-mr-4 -sm:tw-w-full -sm:tw-mb-2'>
                    {`${t(T_KEYS.TITLE)}: ${subscriptionHeader}`}
                </div>
                <SubscriptionStatus status={subscription.status} pauseScheduled={!!subscription.pauseDate} />
                {subscription.pauseDate && isPlanWithBillingPlatformPayment &&
                    renderRemoveScheduledPauseBtn('-sm:tw-hidden tw-ml-auto')}
                {!subscription.pauseDate && isPlanWithBillingPlatformPayment &&
                    renderPauseSubscriptionBtn('-sm:tw-hidden tw-ml-auto')}
            </div>
            <div className='sm:tw-flex tw-mt-2'>
                <div className='tw-flex tw-items-center'>
                    <Icon name='calendar' />
                    <span className='tw-mx-1'>{t(T_KEYS.START_DATE)}</span>
                    <span className='tw-font-bold'>{formatSubscriptionDate(subscription.startDate)}</span>
                </div>
            </div>
            <div className='sm:tw-flex tw-mt-2'>
                {renderUsers(subscription, true)}
                <div className='tw-flex tw-items-center -sm:tw-ml-6 -sm:tw-mt-2'>
                    {renderPrice(subscription)}
                    <Icon
                        button
                        group
                        color='primary'
                        name='user-add'
                        className='tw-ml-2'
                        onClick={() => handleEditSubscription({endOfTerm: false})}
                    />
                </div>
            </div>
            {isPlanWithBillingPlatformPayment &&
                <Fragment>
                    <Divider className='tw-my-2' />
                    {subscription.pauseDate ?
                        <div>
                            <div className='tw-flex tw-items-center tw-mb-2'>
                                <span className='tw-mr-1 tw-text-default'>{t(T_KEYS.PAUSE_SCHEDULED)}</span>
                                <span className='tw-font-bold'>{formatSubscriptionDate(subscription.pauseDate)}</span>
                            </div>
                            <span className='tw-text-default'>{t(T_KEYS.PAUSE_SCHEDULED_HELPER_TEXT)}</span>
                        </div> :
                        <div>
                            <div className='sm:tw-flex'>
                                <div className='tw-flex tw-items-center'>
                                    <Icon name='credit-card' />
                                    {subscription.nextBillingDate &&
                                        <div className='tw-ml-1 tw-flex tw-items-center'>
                                            <span className='tw-mr-1'>{t(T_KEYS.NEXT_BILLING_DATE)}</span>
                                            <span className='tw-font-bold'>{formatSubscriptionDate(subscription.nextBillingDate)}</span>
                                        </div>}
                                </div>
                            </div>
                            <div className='sm:tw-flex tw-mt-2'>
                                {renderUsers(subscriptionNextPeriod, false)}
                                <div className='tw-flex -sm:tw-mt-2 tw-items-center -sm:tw-ml-6'>
                                    {renderPrice(subscriptionNextPeriod)}
                                    <Icon
                                        button
                                        group
                                        color='primary'
                                        size='sm'
                                        name='edit'
                                        className='tw-ml-2'
                                        onClick={() => handleEditSubscription({endOfTerm: true})}
                                    />
                                </div>
                            </div>
                        </div>}
                    {subscriptionNextPeriod.quantity < userCount &&
                        <div className='tw-flex tw-mt-2'>
                            <Icon name='alert-triangle' color='warning' className='tw-mr-1' />
                            <span className='tw-text-warning'>{t(T_KEYS.MIN_USERS_EXCEEDED_NEXT_PERIOD)}</span>
                        </div>}
                    {subscription.pauseDate && renderRemoveScheduledPauseBtn('sm:tw-hidden tw-mt-4 tw-w-full')}
                    {!subscription.pauseDate && renderPauseSubscriptionBtn('sm:tw-hidden tw-mt-4 tw-w-full')}
                    <ConfirmPauseModal {...pauseSubscriptionModalProps} onClose={handlePauseSubscriptionModalClose} />
                    <ConfirmRemovePauseModal
                        open={removePauseSubscriptionModalOpen}
                        onClose={handleConfirmRemovePauseSubscriptionModalClose}
                    />
                    <EditSubscriptionModal {...editModalProps} onClose={handleEditSubscriptionModalClose} />
                </Fragment>}

            {isPlanWithUpgrade && <UpgradePlanModal open={upgradeModalOpen} onClose={() => setUpgradeModalOpen(false)} />}
        </Card>
    );
};